import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { useBrandContext } from '../../context/BrandContext.jsx';
import Button from '../UI/Button.jsx';
import './ErrorBoundary.scss';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Met à jour l'état pour afficher l'UI de fallback
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Enregistre l'erreur pour le debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Ici vous pouvez envoyer l'erreur à un service de monitoring
    // Exemple: Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // Envoyer l'erreur au service de monitoring
      // this.logErrorToService(error, errorInfo);
    }
  }

  handleRetry = () => {
    // Réinitialise l'état d'erreur
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
};

const DEFAULT_FALLBACK_DESIGN = {
  logo: '',
  name: 'Application',
};

// Composant de fallback avec le design dynamique selon la marque
const ErrorFallback = ({ onRetry }) => {
  const brandConfig = useBrandContext();
  const design = brandConfig?.design ?? DEFAULT_FALLBACK_DESIGN;

  const handleGoHome = () => {
    globalThis.location.href = '/';
  };

  const handleReload = () => {
    globalThis.location.reload();
  };

  return (
    <div className="error-boundary-page">
      <div className="error-content">
        {design.logo && (
          <div className="brand-logo-container">
            <img
              src={design.logo}
              alt={`${design.name} Logo`}
              className="brand-logo-top"
            />
          </div>
        )}

        <div className="error-icon">⚠️</div>

        <h1 className="error-title">Une erreur s'est produite</h1>

        <p className="error-description">
          Désolé, nous rencontrons un problème technique inattendu.
        </p>

        <p className="error-suggestion">
          Veuillez réessayer ou contacter votre conseiller si le problème
          persiste.
        </p>

        <div className="error-actions">
          <Button
            variant="secondary"
            className="home-button"
            onClick={handleGoHome}
            aria-label="Retourner à l'accueil"
          >
            Retour à l'accueil
          </Button>

          <Button
            variant="primary"
            className="reload-button"
            onClick={handleReload}
            aria-label="Recharger la page"
          >
            Recharger la page
          </Button>
        </div>

        <div className="help-text">
          <p>Besoin d'aide ?</p>
          <p>Contactez votre conseiller pour toute assistance.</p>
        </div>
      </div>
    </div>
  );
};

ErrorFallback.propTypes = {
  onRetry: PropTypes.func.isRequired,
};

export default ErrorBoundary;
